/**
 * Gallery lightbox (native <dialog>-based).
 *
 * Extends the existing product-hotspot dialog (kept as-is) with:
 * prev/next navigation (click + ArrowLeft/ArrowRight), a counter,
 * touch swipe, and a11y (keyboard-openable thumbnails, focus returned
 * to the trigger on close). Built custom rather than pulling in a
 * lightbox library because the dialog already renders per-image
 * "hotspot" product links (positioned by %) that a generic library
 * would not understand — extending the existing markup/JS was the
 * smaller, easier-to-maintain change.
 *
 * Brief S (2026-08-27): dialog images now hydrate on demand. Each
 * .gallery__dialog-img ships with data-src only (no src) so the
 * browser never fetches any full-resolution photo until the visitor
 * actually opens that slide — showIndex() below sets img.src the
 * first time a given index is shown, then leaves it (browser cache
 * handles repeat visits to the same slide).
 */
export default function gallery() {
	const dialog = document.getElementById('galleryDialog');
	if (!dialog) {
		return;
	}

	const images = document.querySelectorAll('.gallery__tab-img');
	const items = document.querySelectorAll('.gallery__dialog-item');
	const counterEl = dialog.querySelector('[data-gallery-counter]');
	const prevBtn = dialog.querySelector('[data-gallery-prev]');
	const nextBtn = dialog.querySelector('[data-gallery-next]');
	const totalItems = items.length;

	let currentIndex = 0;
	let triggerElement = null;
	let touchStartX = 0;
	let touchStartY = 0;

	function updateUrlParam(index) {
		const newUrl = new URL(window.location.href);
		if (index === null) {
			newUrl.searchParams.delete('gallery');
		} else {
			newUrl.searchParams.set('gallery', index);
		}
		window.history.replaceState({}, '', newUrl);
	}

	// Hydrate the full-res <img> for a single dialog item, only once.
	function hydrateItem(item) {
		if (!item) {
			return;
		}
		const img = item.querySelector('.gallery__dialog-img');
		if (img && !img.src && img.dataset.src) {
			img.src = img.dataset.src;
		}
	}

	function showIndex(index) {
		if (totalItems === 0) {
			return;
		}

		// Wrap around at the boundaries.
		currentIndex = ((index % totalItems) + totalItems) % totalItems;

		items.forEach((item) => (item.style.display = 'none'));
		const activeItem = dialog.querySelector(`.gallery__dialog-item[data-index='${currentIndex}']`);
		if (activeItem) {
			activeItem.style.display = 'block';
			hydrateItem(activeItem);
		}

		if (counterEl) {
			counterEl.textContent = `${currentIndex + 1} / ${totalItems}`;
		}

		updateUrlParam(currentIndex);
	}

	function openDialog(index, trigger) {
		triggerElement = trigger || null;
		showIndex(Number(index) || 0);
		dialog.showModal();
		document.body.style.overflow = 'hidden';

		if (nextBtn) {
			nextBtn.focus();
		}
	}

	function closeDialog() {
		if (dialog.open) {
			dialog.close();
		}
	}

	function showNext() {
		showIndex(currentIndex + 1);
	}

	function showPrev() {
		showIndex(currentIndex - 1);
	}

	// Native 'close' fires on ESC, .close(), and our click-to-close alike —
	// centralising cleanup here means every close path resets state.
	dialog.addEventListener('close', () => {
		document.body.style.overflow = 'auto';
		updateUrlParam(null);

		if (triggerElement) {
			triggerElement.focus();
		}
		triggerElement = null;
	});

	// On page load, check if 'gallery' parameter is present in URL.
	const params = new URLSearchParams(window.location.search);
	const galleryParam = params.get('gallery');
	if (galleryParam !== null) {
		openDialog(galleryParam, null);
	}

	// Open dialog when a thumbnail is clicked or activated via keyboard.
	images.forEach((image) => {
		image.addEventListener('click', function () {
			openDialog(this.dataset.index, this);
		});

		image.addEventListener('keydown', function (event) {
			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				openDialog(this.dataset.index, this);
			}
		});
	});

	if (prevBtn) {
		prevBtn.addEventListener('click', showPrev);
	}

	if (nextBtn) {
		nextBtn.addEventListener('click', showNext);
	}

	// Keyboard navigation while the dialog is open.
	document.addEventListener('keydown', (event) => {
		if (!dialog.open) {
			return;
		}

		if (event.key === 'ArrowRight') {
			showNext();
		} else if (event.key === 'ArrowLeft') {
			showPrev();
		}
		// Escape is handled natively by <dialog>.
	});

	// Touch swipe (mobile).
	dialog.addEventListener('touchstart', (event) => {
		const touch = event.changedTouches[0];
		touchStartX = touch.screenX;
		touchStartY = touch.screenY;
	}, { passive: true });

	dialog.addEventListener('touchend', (event) => {
		const touch = event.changedTouches[0];
		const deltaX = touch.screenX - touchStartX;
		const deltaY = touch.screenY - touchStartY;

		// Ignore mostly-vertical swipes (scrolling) and small movements.
		if (Math.abs(deltaX) < 50 || Math.abs(deltaY) > 80) {
			return;
		}

		if (deltaX < 0) {
			showNext();
		} else {
			showPrev();
		}
	}, { passive: true });

	// Close dialog when clicking outside content or on the close button.
	dialog.addEventListener('click', function (event) {
		const dialogContent = dialog.querySelector('#gallery-dialog-container');
		const isArrow = event.target.closest('[data-gallery-prev], [data-gallery-next]');

		if (isArrow) {
			return;
		}

		if (event.target === this || event.target.hasAttribute('data-close') || !dialogContent.contains(event.target)) {
			closeDialog();
		}
	});

	const closeBtn = dialog.querySelector('[data-close]');
	if (closeBtn) {
		closeBtn.addEventListener('keydown', (event) => {
			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				closeDialog();
			}
		});
	}
}
